import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, fromEvent } from 'rxjs';
import { Minus, Plus, Trash2, X, LucideIconData } from 'lucide-angular';

import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { InputComponent } from '../../../shared/ui/input/input.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { CartDrawerService } from '../../../shared/services/cart-drawer.service';
import { CartService } from '../../../shared/services/cart.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  CartLine,
  PromoCodeError,
  PromoCodeErrorReason,
  PromotionalCodeDiscountType,
  StockConflictItem,
} from '../../../shared/models/cart.model';
import {
  cartLineDiscountPct,
  cartLineGrossSubtotal,
  cartLineHasOffer,
  cartLineOriginalUnitPrice,
  cartLineUnitPrice,
  cartErrorMessage,
} from '../../../shared/utils/cart.utils';
import {
  blockedLineReasonText,
  PERRON_POINTS_COPY,
} from '../../../shared/perron-points/perron-points-copy';
import { CART_COPY, CHECKOUT_COPY, withUnits } from '../../checkout/checkout-copy';
import { formatCrc } from '../../../shared/utils/currency.util';
import { environment } from '../../../../environments/environment';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';
const EMPTY_CART_MASCOT_URL = '/images/logo/empty_cart.png';

const PROMO_ERROR_MESSAGES: Record<PromoCodeErrorReason, string> = {
  invalid: 'Código no válido',
  expired: 'Este código ha caducado',
  min_amount: 'No alcanzas el importe mínimo para este código',
  not_applicable: 'Este código no aplica a tu carrito',
  cart_expired: CART_COPY.gone,
  unknown: 'No se pudo aplicar el código',
};

@Component({
  selector: 'app-cart-drawer',
  imports: [IconComponent, ButtonComponent, InputComponent, BadgeComponent],
  templateUrl: './cart-drawer.component.html',
  styleUrl: './cart-drawer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartDrawerComponent {
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly cartService = inject(CartService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isOpen = this.cartDrawerService.isOpen;
  protected readonly cart = this.cartService.cart;

  /** Conflictos de stock por producto (409 con desglose) que el drawer resalta por línea. */
  protected readonly conflicts = this.cartDrawerService.conflicts;
  protected readonly hasConflicts = this.cartDrawerService.hasConflicts;
  protected readonly conflictCopy = CHECKOUT_COPY.reservation;

  /**
   * Líneas de Acceso VIP que el cliente no puede comprar (R4/R6). El estado se
   * DERIVA del carrito + sesión + saldo en `CartService`, así que aparece solo
   * al recargar con el saldo por debajo del umbral o al cerrar sesión — sin
   * diálogo ni aviso especial al salir.
   */
  protected readonly blockedLines = this.cartService.blockedLines;
  protected readonly hasBlockedLines = this.cartService.hasBlockedLines;
  protected readonly blockedNotice = this.cartService.blockedLinesNotice;
  protected readonly blockedCopy = PERRON_POINTS_COPY.blockedLine;

  protected readonly isEmpty = computed(() => {
    const c = this.cart();
    return !c || c.lines.length === 0;
  });

  protected readonly itemCount = computed(() => this.cart()?.productsQuantity ?? 0);

  // IDs de líneas con operaciones en vuelo (update/remove). Mientras una
  // línea está pending sus controles se deshabilitan para evitar clicks
  // concurrentes que podrían disparar updates fuera de orden.
  private readonly pendingLineIds = signal<Set<number>>(new Set());

  protected readonly promoCodeInput = signal('');
  protected readonly promoPending = signal(false);
  protected readonly promoError = signal<string | null>(null);

  // Código introducido en esta sesión tras éxito de applyPromoCode. Fallback
  // defensivo por si algún endpoint dejase de devolver el objeto completo
  // `cart.promotional_code` — hoy lo devuelve siempre, pero lo mantenemos
  // como red de seguridad.
  private readonly justAppliedCode = signal<string | null>(null);

  // Info a mostrar en el badge de promo activa. Null = no hay promo.
  // Orden de prioridad:
  //  1) `cart.promotional_code` (objeto completo del backend) → code,
  //     description y valor formateado según discount_type.
  //  2) Código tecleado en esta sesión (fallback defensivo).
  //  3) Sólo detectamos el efecto (descuento > 0 o promotional_code_id) →
  //     literal genérico sin más metadatos.
  protected readonly activePromo = computed<{
    code: string;
    description: string | null;
    valueLabel: string | null;
  } | null>(() => {
    const c = this.cart();
    if (!c) return null;

    const pc = c.promotional_code;
    if (pc && typeof pc === 'object' && pc.code) {
      return {
        code: pc.code,
        description: typeof pc.description === 'string' && pc.description ? pc.description : null,
        valueLabel: this.formatDiscountValue(pc.discount_type, pc.discount_value),
      };
    }

    const recent = this.justAppliedCode();
    if (recent) return { code: recent, description: null, valueLabel: null };

    const hasEffect = (c.discounts ?? 0) > 0 || c.promotional_code_id != null;
    if (hasEffect) return { code: 'Código aplicado', description: null, valueLabel: null };

    return null;
  });

  protected readonly mascotUrl = EMPTY_CART_MASCOT_URL;

  protected readonly closeIcon: LucideIconData = X;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly trashIcon: LucideIconData = Trash2;

  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');
  private readonly drawerRef = viewChild<ElementRef<HTMLElement>>('drawerEl');

  // Guarda el elemento que tenía foco antes de abrir el drawer, para
  // devolvérselo al cerrar. Patrón estándar de a11y para diálogos modales.
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor() {
    // ESC cierra el drawer — sólo en browser (SSR no tiene keyboard events).
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key === 'Escape' && this.isOpen()) {
            this.onClose();
          }
        });
    });

    // Focus trap: cuando el drawer está abierto, Tab/Shift+Tab debe
    // circular SOLO entre los elementos focusables dentro del drawer.
    // Sin esto, el user puede salir del drawer y navegar elementos
    // detrás del overlay, lo que rompe el contrato de aria-modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;

          const drawer = this.drawerRef()?.nativeElement;
          if (!drawer) return;

          const focusables = this.getFocusableElements(drawer);
          if (focusables.length === 0) return;

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          // Caso defensivo: el foco salió del drawer (ej. un click fuera,
          // o un elemento externo recuperó foco por CD). Al pulsar Tab,
          // devolvemos el foco al primer focusable del drawer.
          if (!active || !drawer.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });

    // Focus management al abrir/cerrar el drawer:
    //  - Al abrir: guarda el elemento con foco actual y mueve el foco al
    //    botón close (queueMicrotask evita el blackhole de `visibility:
    //    hidden` si el focus se pide en el mismo tick que la clase --open).
    //  - Al cerrar: devuelve el foco al elemento previamente enfocado
    //    (típicamente el botón del badge del cart en el header).
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      if (this.isOpen()) {
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => this.closeButtonRef()?.nativeElement.focus());
      } else if (this.previouslyFocusedElement) {
        // Restore focus solo si el elemento sigue en el DOM y es focusable.
        // preventScroll evita el salto visual en mobile cuando el browser
        // hace scroll automático al elemento focusado (el badge sticky del
        // header ya está visible, no necesita scroll).
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) {
            el.focus({ preventScroll: true });
          }
        });
      }
    });
  }

  protected onClose(): void {
    this.cartDrawerService.close();
  }

  protected onExploreClick(): void {
    this.cartDrawerService.close();
    this.router.navigate(['/tienda']);
  }

  protected onCheckoutClick(): void {
    this.cartDrawerService.close();
    this.router.navigate(['/checkout']);
  }

  protected isLinePending(lineId: number): boolean {
    return this.pendingLineIds().has(lineId);
  }

  protected isLineBlocked(lineId: number): boolean {
    return this.blockedLines().has(lineId);
  }

  /** Motivo del bloqueo, con la variante de umbral 0 («Requiere iniciar sesión»). */
  protected blockedReason(lineId: number): string {
    return blockedLineReasonText(this.blockedLines().get(lineId) ?? 0);
  }

  protected onDecrement(line: CartLine): void {
    // quantity = 1 y el usuario pulsa `-` → tratamos como eliminar la línea,
    // en vez de enviar una update con quantity=0 que el backend rechaza.
    if (line.quantity <= 1) {
      this.onRemove(line);
      return;
    }
    this.updateQuantity(line, line.quantity - 1);
  }

  protected onIncrement(line: CartLine): void {
    this.updateQuantity(line, line.quantity + 1);
  }

  protected onRemove(line: CartLine): void {
    if (this.isLinePending(line.id)) return;
    this.markPending(line.id);

    this.cartService
      .removeLine(line.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.clearPending(line.id)),
      )
      .subscribe({
        // La línea desaparece: su conflicto queda resuelto.
        next: () => this.cartDrawerService.clearConflict(line.product_id),
        error: (err) => {
          const message = cartErrorMessage(err, 'No pudimos eliminar el producto.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  private updateQuantity(line: CartLine, newQuantity: number): void {
    if (this.isLinePending(line.id)) return;
    this.markPending(line.id);

    this.cartService
      .updateLine(line.id, line.product_id, newQuantity)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.clearPending(line.id)),
      )
      .subscribe({
        // Si la nueva cantidad ya cabe en las unidades libres, el conflicto de esa línea se resuelve.
        next: () => this.resolveConflictAfterUpdate(line.product_id, newQuantity),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP al SUBIR cantidad (R9b): el diálogo
          // ya lo explica. Bajar nunca llega aquí porque nunca se bloquea.
          const message = cartErrorMessage(err, 'No pudimos actualizar la cantidad.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  // --- Conflictos de stock (resaltado + auto-ajuste) ----------------------------------------

  /**
   * Conflicto de la línea (si su producto está en el desglose del 409), o null. Indexa por
   * `product_id`, lo que asume **≤1 línea por producto** en el carrito: invariante garantizado por el
   * backend, que al añadir un producto ya presente **incrementa la línea existente** en vez de duplicarla
   * (`CartLineController::newWebCartLine`), y por el 409, que agrega las cantidades por `product_id`.
   */
  protected lineConflict(line: CartLine): StockConflictItem | null {
    return this.conflicts().get(line.product_id) ?? null;
  }

  /** Etiqueta de unidades libres de una línea en conflicto (o «sin unidades libres» si 0). */
  protected conflictFreeLabel(available: number): string {
    return available > 0
      ? withUnits(this.conflictCopy.conflictFreeUnits, available)
      : this.conflictCopy.conflictNoneLeft;
  }

  /** CTA de auto-ajuste: «Dejar N». */
  protected conflictAdjustLabel(available: number): string {
    return withUnits(this.conflictCopy.conflictAdjustCta, available);
  }

  /**
   * Auto-ajuste desde el resaltado de conflicto: deja la cantidad en las unidades libres; si no queda
   * ninguna, elimina la línea. Reutiliza el mismo camino que los controles +/- (respeta isLinePending).
   */
  protected onLeaveFree(line: CartLine, available: number): void {
    if (available > 0) {
      this.updateQuantity(line, available);
      return;
    }
    this.onRemove(line);
  }

  private resolveConflictAfterUpdate(productId: number, newQuantity: number): void {
    const conflict = this.conflicts().get(productId);
    if (conflict && newQuantity <= conflict.available) {
      this.cartDrawerService.clearConflict(productId);
    }
  }

  protected onPromoInputChange(value: string): void {
    this.promoCodeInput.set(value);
    // Limpiamos el error previo en cuanto el usuario edita el input: si se
    // equivocó, que no arrastre el mensaje rojo mientras vuelve a teclear.
    if (this.promoError()) this.promoError.set(null);
  }

  protected onApplyPromo(): void {
    const code = this.promoCodeInput().trim();
    if (!code || this.promoPending()) return;

    this.promoPending.set(true);
    this.promoError.set(null);

    this.cartService
      .applyPromoCode(code)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.promoPending.set(false)),
      )
      .subscribe({
        next: () => {
          this.justAppliedCode.set(code);
          this.promoCodeInput.set('');
        },
        error: (err) => {
          const reason: PromoCodeErrorReason =
            err instanceof PromoCodeError ? err.reason : 'unknown';
          // Con el carrito limpio el drawer pasa a su estado vacío y el
          // `promoError` (dentro del `@else` de `isEmpty()`) no se vería nunca:
          // este aviso va en toast.
          if (reason === 'cart_expired') {
            this.toastService.show({ type: 'error', message: PROMO_ERROR_MESSAGES[reason] });
            return;
          }
          this.promoError.set(PROMO_ERROR_MESSAGES[reason]);
        },
      });
  }

  protected onRemovePromo(): void {
    if (this.promoPending()) return;
    this.promoPending.set(true);

    this.cartService
      .removePromoCode()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.promoPending.set(false)),
      )
      .subscribe({
        next: () => {
          this.justAppliedCode.set(null);
        },
        error: (err) => {
          const message = cartErrorMessage(err, 'No pudimos quitar el código.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  protected lineImageUrl(line: CartLine): string {
    const first = line.product?.images?.[0];
    if (!first) return FALLBACK_IMAGE_URL;
    return `${environment.imagesUrl}products/${first.name}`;
  }

  protected formatCRC(amount: number): string {
    return formatCrc(amount);
  }

  // Precios de línea con IVA (paridad con el catálogo). Delegan en cart.utils;
  // `line.sale_price`/`line.subtotal` son netos y no deben pintarse directo.
  protected lineUnitPrice(line: CartLine): number {
    return cartLineUnitPrice(line);
  }

  protected lineSubtotal(line: CartLine): number {
    return cartLineGrossSubtotal(line);
  }

  protected lineHasOffer(line: CartLine): boolean {
    return cartLineHasOffer(line);
  }

  protected lineOriginalPrice(line: CartLine): number | null {
    return cartLineOriginalUnitPrice(line);
  }

  protected lineDiscountPct(line: CartLine): number {
    return Math.round(cartLineDiscountPct(line));
  }

  private formatDiscountValue(
    type: PromotionalCodeDiscountType | undefined,
    value: number | undefined,
  ): string | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    if (type === 'percentage') return `${value}%`;
    if (type === 'fixed') return this.formatCRC(value);
    return null;
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }

  private markPending(id: number): void {
    this.pendingLineIds.update((set) => {
      const next = new Set(set);
      next.add(id);
      return next;
    });
  }

  private clearPending(id: number): void {
    this.pendingLineIds.update((set) => {
      const next = new Set(set);
      next.delete(id);
      return next;
    });
  }

  // Devuelve los elementos focusables visibles dentro de un contenedor.
  // Filtra los disabled, tabindex="-1" y ocultos (offsetParent null —
  // cubre display:none y ancestros colapsados).
  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null,
    );
  }
}
